// @flow
import { Text } from 'grommet';
import * as React from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { withLeaflet } from 'react-leaflet';
import type { RouterHistory, Match } from 'react-router-dom';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { connectAPI } from 'speed-js-react';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import cableAndFiberMessages from 'esm-web-pro/messages/cableAndFiberMessages';
import RowActions from 'esm-web-pro/components/layout/sidePanel/RowActions';
import MeatballsIcon from 'esm-web-pro/components/layout/sidePanel/icons/MeatballsIcon';
import ContentRow from 'esm-web-pro/components/layout/sidePanel/ContentRow';
import UseHighlightCable from 'esm-web-pro/containers/pages/network/objects/cable/useHighlightCable';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import type { LeafletContext } from 'react-leaflet';
import {
    numberPointsCallbacksCable,
} from 'esm-web-pro/containers/pages/network/objects/cable/MapCable';
import type { Cable } from 'esm-web-pro/types/networks';
import ValueText from 'esm-web-pro/components/layout/sidePanel/ValueText';

type BaseCable = {
    id: number,
    uuid: string,
    ref: string
};

type Props = {
    cables: Array<BaseCable>,
    history: RouterHistory,
    match: Match,
    padLevel?: 1 | 2 | 3,
    intl: IntlShape,
    editor: BaseEditor,
    leaflet: LeafletContext,
    clicked: string,
    setClicked: Function,
    getPath: (id: string, map: Map) => Promise<Object>,
    map: Map,
}

const CableList = (props: Props) => {
    const {
        padLevel, intl, editor, leaflet, clicked, setClicked, getPath, map,
    } = props;


    const highlightCable = (cable) => {
        editor.setLoading(true);
        getPath(cable.uuid, map).then((vCable: Cable) => {
            editor.setLoading(false);
            editor.unregisterAllLoadingCallback();

            const { cable_path: cablePath } = vCable;
            const cableCoordinates = cablePath.path.map((c) => [c[1], c[0]]);
            const highlightCableCallback = UseHighlightCable(cableCoordinates);
            editor.registerLoadingCallback(highlightCableCallback);
            editor.registerMultipleLoadingCallbacks(numberPointsCallbacksCable(vCable));
            if (leaflet.map) {
                leaflet.map.highlightedGeometry = {
                    bounds: cableCoordinates, center: null,
                };
                leaflet.map.panInsideBounds(cableCoordinates);
            }
            editor.reloadFeatures();
            const rowId = `Cable ${vCable.id}`;
            setClicked(rowId);
        });
    };

    return (props.cables.map<React.Node>((cable: Cable) => (
        <ContentRow
            onClick={() => highlightCable(cable)}
            padLevel={padLevel}
            key={cable.uuid}
            rowId={`Cable ${cable.id}`}
            clickedRowIdentity={clicked}
            object={cable}
            actions={(
                <RowActions
                    actions={[{
                        key: cable.uuid,
                        icon: <MeatballsIcon />,
                        size: '20px',
                        width: '3px',
                        callback: () => {
                            const path = props.match.url.split('/layer');
                            props.history.push(`${path[0]}/layer/cable/${cable.identity}`);
                        },
                        tooltip: intl.formatMessage(actionMessages.more),
                    }]}
                />
            )}
        >
            <ValueText>
                {`${intl.formatMessage(cableAndFiberMessages.cableLong)} ${cable.ref}`}
            </ValueText>
        </ContentRow>
    ))
    );
};

CableList.defaultProps = {
    padLevel: undefined,
};

const apiCallback = (apiClient: ApiClient) => ({
    getPath: {
        send: (id: string, map: Object) => {
            if (map.is_draft) {
                return apiClient.resources.cable.path(id, { is_draft: map.uuid });
            }
            return apiClient.resources.cable.path(id);
        },
    },
});

export default connectAPI(apiCallback)(withLeaflet(injectIntl(withRouter(CableList))));
